/**
 * Beneficiary Health & Care Profile
 *
 * Read-only display of the beneficiary's onboarding survey answers (gated by an allowlist,
 * scrubbed of PII), grouped into Care, Medical, and Vitals sections. Renders nothing when
 * there are no displayable answers.
 */

import { useMemo } from 'react'
import { HeartPulse } from 'lucide-react'
import { useLocalization } from '@/contexts/LocalizationContext'
import {
  getBeneficiarySurveyRows,
  BENEFICIARY_SURVEY_GROUPS,
} from '@/utils/beneficiarySurveyDisplay'

export function BeneficiaryHealthProfileCard({
  surveyData,
}: {
  surveyData: string | null | undefined
}) {
  const { t, locale } = useLocalization()

  const rows = useMemo(
    () => getBeneficiarySurveyRows(surveyData, locale === 'ar' ? 'ar' : 'en'),
    [surveyData, locale]
  )

  if (rows.length === 0) return null

  const rowByCode = new Map(rows.map((row) => [row.code, row]))

  const groups = BENEFICIARY_SURVEY_GROUPS.map((group) => ({
    titleKey: group.titleKey,
    rows: group.codes.map((code) => rowByCode.get(code)).filter((row) => row != null),
  })).filter((group) => group.rows.length > 0)

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/50 px-5 py-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-100 text-rose-600">
          <HeartPulse className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{t('orderDetails.healthProfile')}</h3>
          <p className="text-xs text-gray-500">{t('orderDetails.healthProfileSubtitle')}</p>
        </div>
      </div>

      <div className="p-5 space-y-6">
        {groups.map((group) => (
          <div key={group.titleKey}>
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
              {t(`orderDetails.${group.titleKey}`)}
            </h4>
            <div className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
              {group.rows.map((row) => (
                <div
                  key={row!.code}
                  className="flex items-start justify-between gap-4 border-b border-gray-50 py-1.5 last:border-0"
                >
                  <span className="text-sm text-gray-500">{row!.label}</span>
                  <span className="text-sm font-medium text-gray-900 text-end">
                    {row!.valueText}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
